/* @flow */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import pluralize from 'pluralize';
import { compose, withProps } from 'recompose';
import styled from '@emotion/styled';
import { padding } from 'polished';
import { hover } from 'app/utils/cssUtils';
import withAPICollection from 'app/hoc/withAPICollection';
import { getPrizes } from 'app/api/challenges';
import Cell from 'app/components/static/Cell';
import Loader from 'app/components/commons/Loader';
import Tappable from 'app/components/commons/Tappable';
import t from 'app/utils/translate';
import Rows from 'app/components/layout/Rows';
import Spinner from 'app/components/commons/Spinner';

type Props = {
  currencyName: string,
  currentPage: number,
  hasNextPage: boolean,
  isLoading: boolean,
  onFetchPrizes: () => void,
  payoutMessage: string,
  prizes: Array<*>,
  scheme: string
};

const Content = styled.div({
  position: 'relative'
});

const PrizeList = styled.div(({ theme }) => ({
  paddingBottom: theme.spacing.large,
  position: 'relative'
}));

const Pagination = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: '2em', // fix height to avoid "jumpiness"
  justifyContent: 'space-between',
  // $FlowIgnore
  ...padding('0', theme.spacing.small),
  '&:empty': {
    height: 0,
    padding: 0
  }
}));

const PageLink = styled.span(({ theme }) => ({
  textTransform: 'uppercase',
  transition: 'color 0.2s',
  ...hover({
    color: theme.color.basis5
  })
}));

const Prev = styled.span({
  display: 'flex'
});

const Next = styled.span({
  display: 'flex',
  marginLeft: 'auto'
});

const PayoutDescription = styled.div(({ theme }) => ({
  paddingTop: theme.spacing.xsmall,
  [theme.mediaQuery.desktopAndUp]: {
    padding: theme.spacing.default,
    paddingTop: theme.spacing.xsmall
  }
}));

const PrizesContent: React.FC<Props> = ({
  currencyName,
  currentPage,
  hasNextPage,
  isLoading,
  onFetchPrizes,
  payoutMessage,
  prizes,
  scheme
}: Props) => {
  const [isNextLoading] = React.useState(false);
  const [isPrevLoading] = React.useState(false);

  const handleFetchPrizes = data => {
    if (prizes.length === 0) {
      onFetchPrizes(data);
    }
  };

  useEffect(handleFetchPrizes, []);

  const handlePrev = () => {
    onFetchPrizes({ page: currentPage - 1 });
  };

  const handleNext = () => {
    onFetchPrizes({ page: currentPage + 1 });
  };

  if (isLoading) {
    return <Loader kind={scheme === 'light' ? 'dark' : 'light'} />;
  }

  if (prizes.length === 0) {
    return <div>{t('Prize details unavailable')}</div>;
  }

  return (
    <Content>
      <Rows mobile={1} desktop={2}>
        <PrizeList>
          {prizes.map(prize => (
            <Cell title={prize.position} key={prize.position}>
              {prize.label}
            </Cell>
          ))}
        </PrizeList>
        <PayoutDescription>
          {payoutMessage}
          <p>All amounts are displayed in {pluralize(currencyName)}</p>
        </PayoutDescription>
      </Rows>
      <Pagination>
        {currentPage > 1 && (
          <Prev>
            {isPrevLoading ? (
              <Spinner />
            ) : (
              <Tappable component={PageLink} onTap={handlePrev}>
                Previous prizes
              </Tappable>
            )}
          </Prev>
        )}
        {hasNextPage && (
          <Next>
            {isNextLoading ? (
              <Spinner />
            ) : (
              <Tappable component={PageLink} onTap={handleNext}>
                Next prizes
              </Tappable>
            )}
          </Next>
        )}
      </Pagination>
    </Content>
  );
};

const apiWrapper = compose(
  withAPICollection({
    fetcher: getPrizes
  }),
  withProps(({ challengeId, currentPage, hasNextPage, items, loading, onFetch, prizes }) => ({
    currentPage,
    hasNextPage,
    isLoading: loading,
    prizes: prizes.length > 0 ? prizes : items,
    onFetchPrizes(query) {
      onFetch(challengeId, query);
    }
  }))
);

const connector = connect(({ settings }) => ({
  currencyName: get('currency.name')(settings) || ''
}));

export default compose(apiWrapper, connector)(PrizesContent);
